import { cache } from 'react';

import { buildDirectBackendUrl } from '@/lib/direct-backend-api';
import type {
  MediaAuthorsApiAuthorPageResponse,
  MediaAuthorsApiCatalogResponse,
  MediaAuthorsApiIndexableResponse,
  MediaAuthorsApiSeoHubResponse,
} from '../api/media-authors-api.types';
import type {
  MediaAuthorPageData,
  MediaAuthorProfile,
  MediaAuthorsCatalog,
  MediaAuthorsIndexablePage,
} from '../types';
import {
  mapMediaAuthorPage,
  mapMediaAuthorsCatalog,
  mapMediaAuthorsIndexable,
  MEDIA_AUTHORS_DEFAULT_ARTICLES_LIMIT,
} from '../utils/map-media-author.util';

const MEDIA_AUTHORS_HUB_KEY = 'all_media_authors';

async function fetchJson<T>(url: string): Promise<{ ok: true; data: T } | { ok: false; status: number }> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    return { ok: false, status: response.status };
  }
  const data = (await response.json()) as T;
  return { ok: true, data };
}

/**
 * Loads the media authors catalog with SEO hub meta (SSR).
 * Returns null when the SEO hub is disabled or missing (public 404).
 */
export const getMediaAuthorsCatalogSSR = cache(async (): Promise<MediaAuthorsCatalog | null> => {
  const [authorsResult, hubResult] = await Promise.all([
    fetchJson<MediaAuthorsApiCatalogResponse>(buildDirectBackendUrl('/api/v1/media/authors')),
    fetchJson<MediaAuthorsApiSeoHubResponse>(
      buildDirectBackendUrl(`/api/v1/seo/hubs/${MEDIA_AUTHORS_HUB_KEY}`),
    ),
  ]);
  if (!hubResult.ok) {
    return null;
  }
  if (!authorsResult.ok) {
    throw new Error(`Media authors catalog failed with status ${authorsResult.status}`);
  }
  return mapMediaAuthorsCatalog(authorsResult.data, hubResult.data);
});

/**
 * Loads an author profile with paginated articles (SSR).
 * Returns null on 404 (missing, inactive, or no username).
 */
export const getMediaAuthorPageSSR = cache(
  async (
    username: string,
    page: number = 1,
    limit: number = MEDIA_AUTHORS_DEFAULT_ARTICLES_LIMIT,
  ): Promise<MediaAuthorPageData | null> => {
    const safePage = page > 0 ? page : 1;
    const safeLimit = limit > 0 ? limit : MEDIA_AUTHORS_DEFAULT_ARTICLES_LIMIT;
    const query = new URLSearchParams({
      page: String(safePage),
      limit: String(safeLimit),
    });
    const url = buildDirectBackendUrl(
      `/api/v1/media/authors/${encodeURIComponent(username)}?${query.toString()}`,
    );
    const result = await fetchJson<MediaAuthorsApiAuthorPageResponse>(url);
    if (!result.ok) {
      if (result.status === 404) {
        return null;
      }
      throw new Error(`Media author page failed with status ${result.status}`);
    }
    return mapMediaAuthorPage(result.data, safeLimit);
  },
);

/**
 * Loads only the author profile (first page, no articles) for metadata helpers.
 */
export async function getMediaAuthorBySlugSSR(username: string): Promise<MediaAuthorProfile | null> {
  const pageData = await getMediaAuthorPageSSR(username, 1, 1);
  return pageData?.profile ?? null;
}

type GetMediaAuthorsIndexableInput = {
  readonly page?: number;
  readonly limit?: number;
  readonly siteUrl: string;
};

/**
 * Loads indexable author URLs for sitemap generation.
 */
export async function getMediaAuthorsIndexableSSR(
  input: GetMediaAuthorsIndexableInput,
): Promise<MediaAuthorsIndexablePage> {
  const page = input.page && input.page > 0 ? input.page : 1;
  const limit = input.limit && input.limit > 0 ? input.limit : 100;
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const url = buildDirectBackendUrl(`/api/v1/media/authors/indexable?${query.toString()}`);
  const result = await fetchJson<MediaAuthorsApiIndexableResponse>(url);
  if (!result.ok) {
    throw new Error(`Media authors indexable failed with status ${result.status}`);
  }
  return mapMediaAuthorsIndexable(result.data, input.siteUrl);
}
